import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { Feature, Geometry, GeoJsonProperties } from 'geojson';
import { generate } from 'short-uuid';
import TypeaheadSearch from './typeaheadSearch';
import { TypeaheadSearchResultItem } from './typeaheadSearchResult';
import {
  Agent,
  City,
  LinerServiceAdminContext,
  LinerServiceAdminSettings,
  ShippingCompany,
  Terminal,
} from './linerServiceAdmin';
import LinerServiceDeleteDialog from './linerServiceDeleteDialog';
import LinerServiceFrequencySelector from './linerServiceFrequencySelector';

export interface LinerServiceRouteListProps {
  feature: Feature<Geometry, GeoJsonProperties>;
  mapboxMapFeatures: Feature<Geometry, GeoJsonProperties>[];
  agents: Agent[];
  terminals: Terminal[];
  shippingCompanies: ShippingCompany[];
  cities: City[];
}

export interface Route {
  id: string | null;
  nodeId: string | null;
  agent?: TypeaheadSearchResultItem | null;
  shippingCompany?: TypeaheadSearchResultItem | null;
  routeFrequency?: TypeaheadSearchResultItem | null;
  terminal?: TypeaheadSearchResultItem | null;
  roro: boolean;
  container: boolean;
  bulk: boolean;
  edit?: boolean;
}

export interface LocalStorageRoute {
  route: Route;
  removed: boolean;
}

// ----------------------------------------
//  RouteList component
// ----------------------------------------

const LinerServiceRouteList = ({
  feature,
  agents,
  terminals,
  shippingCompanies,
  cities,
  mapboxMapFeatures,
}: LinerServiceRouteListProps) => {
  const routeControlsRef = useRef<HTMLDivElement | null>(null);

  const [newRoute, setNewRoute] = useState<Route>({
    id: null,
    nodeId: null,
    agent: null,
    shippingCompany: null,
    routeFrequency: null,
    terminal: null,
    container: false,
    roro: false,
    bulk: false,
    edit: false,
  });
  const [routes, setRoutes] = useState<Route[]>([]);
  const [routeDefaultValues, setRouteDefaultValues] = useState<Route[]>([]);
  const [featureId, setFeatureId] = useState<string | null>(null);

  const [activeDeleteDialog, setActiveDeleteDialog] = useState<string | null>(null);

  const { localMapFeatures, setLocalMapFeatures, locale } =
    useContext<LinerServiceAdminSettings>(LinerServiceAdminContext);

  // ---------------------------------------------------------------
  //  USE EFFECT - Feature - listen for feature change
  // ---------------------------------------------------------------

  useEffect(() => {
    if (feature?.properties?.routes) {
      setRoutes(_.cloneDeep(feature.properties.routes));
      setRouteDefaultValues(_.cloneDeep(feature.properties.routes));
    }

    setFeatureId(String(feature?.properties?.nodeId));

    setNewRoute({
      ...newRoute,
      ...{
        id: generate(),
        nodeId: String(feature?.properties?.nodeId),
      },
    });
  }, [feature]);

  // ---------------------------------------------------------------
  //  USE EFFECT - Routes - listen for route list change
  // ---------------------------------------------------------------

  useEffect(() => {
    const properties = feature.properties as any;
    if (routes) {
      properties.roro = routes.some((route) => route.roro);
      properties.container = routes.some((route) => route.container);
      properties.bulk = routes.some((route) => route.bulk);
      properties.routes = routes || [];
    }
    setLocalMapFeatures([...localMapFeatures]);
  }, [routes]);

  const isRouteUploaded = (route: Route) => {
    if (mapboxMapFeatures && feature) {
      const featureMatch = mapboxMapFeatures.find((mapboxFeature) => mapboxFeature.id === feature.id);
      const routes = featureMatch?.properties?.routes as Route[];

      const externalRoute = routes?.find((mbRoute) => mbRoute.id === route.id);

      if (externalRoute) {
        return _.isEqual(externalRoute, route);
      }
      return false;
    }

    return false;
  };

  // --------------------------------------------------------------------
  //  addRoute - add new route to the newRoute object
  //  -------------------------------------------------------------------

  const addRoute = () => {
    if (featureId) {
      const routeToAdd = _.cloneDeep(newRoute);
      routes.push(routeToAdd);
      setRoutes([...routes]);
      saveToLocalStorage(routeToAdd);
      setRouteDefaultValues(_.cloneDeep(routes));

      setNewRoute({
        id: generate(),
        nodeId: featureId,
        agent: null,
        shippingCompany: null,
        routeFrequency: null,
        terminal: null,
        container: false,
        roro: false,
        bulk: false,
        edit: false,
      });
    }
  };

  // -----------------------------------------------------------------------------------------
  // saveToLocalStorage -
  // -----------------------------------------------------------------------------------------

  const saveToLocalStorage = (route: Route) => {
    // Check if route is already uploaded to MapBox
    let saveValid = true;

    const uploadedRoutes = mapboxMapFeatures.find((mbFeature) => mbFeature.properties?.nodeId === route.nodeId)
      ?.properties?.routes as Route[];

    const uploadedRoute = uploadedRoutes?.find((uRoute) => uRoute.id == route.id);

    // Do not save to local storage if the route is exactly the same as the one uploaded to MapBox
    if (uploadedRoute && _.isEqual(uploadedRoute, route)) {
      saveValid = false;
    }
    if (saveValid) {
      const localStorageData = localStorage.getItem('ghabRoutes');
      const localStorageRoutes: LocalStorageRoute[] = localStorageData ? JSON.parse(localStorageData) : [];

      const indexInLocalStorage = localStorageRoutes.findIndex((lsRoute) => lsRoute.route.id === route.id);

      if (indexInLocalStorage >= 0) {
        localStorageRoutes.splice(indexInLocalStorage, 1);
      }

      localStorageRoutes.push({ route, removed: false });

      localStorage.setItem('ghabRoutes', JSON.stringify(localStorageRoutes));
    }
  };

  // -----------------------------------------------------------------------------------------
  // syncLocalStorage - syncs the routes data between local storage and
  // -----------------------------------------------------------------------------------------

  const removeFromLocalStorage = (route: Route) => {
    const localStorageData = localStorage.getItem('ghabRoutes');
    const localStorageRoutes: LocalStorageRoute[] = localStorageData ? JSON.parse(localStorageData) : [];

    const storageRoute = localStorageRoutes.find((lsRoute) => lsRoute.route.id === route.id);

    if (storageRoute) {
      storageRoute.removed = true;
    } else {
      localStorageRoutes.push({ route, removed: true });
    }

    localStorage.setItem('ghabRoutes', JSON.stringify(localStorageRoutes));
  };

  // --------------------------------------------------------------------
  //  setItemValue - updates a value in the route or new route object
  //                 depending if a new one is being created
  //  -------------------------------------------------------------------

  const setItemValue = (id: string, item: TypeaheadSearchResultItem | TypeaheadSearchResultItem[], route?: Route) => {
    const key = id.split('_')[0];

    if (item) {
      if (route) {
        (route as any)[key] = _.cloneDeep(item);
      } else {
        (newRoute as any)[key] = _.cloneDeep(item);
      }
    }
  };
  // ------------------------------------------------------------------------
  //  setCheckboxValue - updates a checkbox value in the route or new route
  //                     object depending if a new one is being created
  //  -----------------------------------------------------------------------

  const setCheckboxValue = (id: string, checked: boolean, route?: Route) => {
    const key = id.split('_')[0];
    if (route) {
      (route as any)[key] = checked;
    } else {
      (newRoute as any)[key] = checked;
    }
  };

  // --------------------------------------------------------------------
  //  toggleEditMode - toggles edit mode on/off on a Route
  //  -------------------------------------------------------------------

  const toggleEditMode = (route: Route) => {
    route.edit = !route.edit;
    if (!route.edit) {
      saveToLocalStorage(route);
      setRouteDefaultValues(_.cloneDeep(routes));
    }
    setRoutes([...routes]);
  };

  const closeEditMode = (route: Route) => {
    const defaultRoute = routeDefaultValues.find((defaultRoute) => defaultRoute.id === route.id);

    if (defaultRoute) {
      route.agent = defaultRoute.agent;
      route.shippingCompany = defaultRoute.shippingCompany;
      route.routeFrequency = defaultRoute.routeFrequency;
      route.terminal = defaultRoute.terminal;
      route.container = defaultRoute.container;
      route.roro = defaultRoute.roro;
      route.bulk = defaultRoute.bulk;
    }
    route.edit = false;
    setRoutes([...routes]);
  };

  // --------------------------------------------------------------------
  //  deleteRoute - deletes a route
  //  -------------------------------------------------------------------

  const deleteRoute = (route: Route) => {
    const index = routes.findIndex((routeListItem) => route.id === routeListItem.id);
    if (index >= 0) {
      routes.splice(index, 1);
    }

    removeFromLocalStorage(route);
    setRoutes([...routes]);
    setActiveDeleteDialog(null);
  };

  // ------------------------------------------------------------------------
  //  getCargoText - generates a string for the diffferent container values
  //  -----------------------------------------------------------------------

  const getCargoText = (route: Route) => {
    if (route.roro && !route.container) {
      return 'Roro/Fordon';
    }
    if (!route.roro && route.container) {
      return 'Container';
    }

    if (route.roro && route.container) {
      return 'Roro/Fordon, Container';
    }

    return '-';
  };

  // ------------------------------------------------------------------------
  //  JSX
  //  -----------------------------------------------------------------------

  return (
    <div className="liner-service-expanded-content">
      <div className="liner-service-admin-routes">
        <div className="routes-list-headers">
          <div className="start-filler"></div>
          <div>Agent:</div>
          <div>Rederi:</div>
          <div>Frekvens:</div>
          <div>Terminal:</div>
          <div>Last:</div>
          <div className="end-filler"></div>
        </div>
        <div className="routes-list-rows">
          {routes?.length ? (
            routes?.map((route: Route, index) => (
              <div key={index} className="routes-list-item">
                <div className="uploaded-icon">
                  {isRouteUploaded(route) ? (
                    <span title="Uppladdad rutt" className="icon bi bi-check-circle-fill"></span>
                  ) : null}
                </div>
                <div className="route-value-form">
                  {route.edit ? (
                    <TypeaheadSearch
                      id={`agent_${route.id}`}
                      resultList={agents.map((agent) => {
                        return { id: agent.id, value: agent.name, label: agent.name, link: agent.link };
                      })}
                      defaultValue={route.agent}
                      valueSelected={(item: TypeaheadSearchResultItem) =>
                        setItemValue(`agent_${route.id}`, item, route)
                      }
                    />
                  ) : (
                    <span>{route.agent?.label}</span>
                  )}
                </div>
                <div className="route-value-form">
                  {route.edit ? (
                    <TypeaheadSearch
                      id={`shippingCompany_${route.id}`}
                      resultList={shippingCompanies.map((shippingCompany) => {
                        return {
                          id: shippingCompany.id,
                          value: shippingCompany.name,
                          label: shippingCompany.name,
                          link: shippingCompany.link,
                        };
                      })}
                      defaultValue={route.shippingCompany}
                      valueSelected={(item: TypeaheadSearchResultItem) =>
                        setItemValue(`shippingCompany_${route.id}`, item, route)
                      }
                    />
                  ) : (
                    <div>{route.shippingCompany?.label}</div>
                  )}
                </div>
                <div className="route-value-form">
                  {route.edit ? (
                    <LinerServiceFrequencySelector
                      id={`routeFrequency_${route.id}`}
                      valueSelected={(item: TypeaheadSearchResultItem) =>
                        setItemValue(`routeFrequency_${route.id}`, item, route)
                      }
                      defaultValue={route.routeFrequency}
                    />
                  ) : (
                    <div>{route.routeFrequency?.label}</div>
                  )}
                </div>
                <div className="route-value-form">
                  {route.edit ? (
                    <TypeaheadSearch
                      id={`terminal_${route.id}`}
                      resultList={terminals.map((terminal) => {
                        return {
                          id: terminal.id,
                          value: terminal.name,
                          label: terminal.name,
                          link: terminal.link,
                        };
                      })}
                      defaultValue={route.terminal}
                      valueSelected={(item: TypeaheadSearchResultItem) =>
                        setItemValue(`terminal_${route.id}`, item, route)
                      }
                    />
                  ) : (
                    <div>{route.terminal?.label}</div>
                  )}
                </div>
                <div className="route-value-form">
                  {route.edit ? (
                    <div className="cargo-group">
                      <div>
                        <label htmlFor={`container_${route.id}`}>Container:</label>
                        <input
                          type="checkbox"
                          id={`container_${route.id}`}
                          name="Last"
                          defaultChecked={route.container}
                          onChange={(e) => setCheckboxValue(`container_${route.id}`, e.currentTarget.checked, route)}
                        />
                      </div>
                      <div>
                        <label htmlFor={`roro_${route.id}`}>Roro/Fordon:</label>
                        <input
                          type="checkbox"
                          id={`roro_${route.id}`}
                          name="Roro/Fordon"
                          defaultChecked={route.roro}
                          onChange={(e) => setCheckboxValue(`roro_${route.id}`, e.currentTarget.checked, route)}
                        />
                      </div>
                      <div>
                        <label htmlFor={`bulk_${route.id}`}>Bulk:</label>
                        <input
                          type="checkbox"
                          id={`bulk_${route.id}`}
                          name="Bulk"
                          defaultChecked={route.bulk}
                          onChange={(e) => setCheckboxValue(`bulk_${route.id}`, e.currentTarget.checked, route)}
                        />
                      </div>
                    </div>
                  ) : (
                    <div>{getCargoText(route)}</div>
                  )}
                </div>
                <div ref={routeControlsRef} className="route-controls">
                  <button
                    title="edit route"
                    type="button"
                    className={`edit-route-button ${route.edit ? 'edit' : ''}`}
                    onClick={() => toggleEditMode(route)}
                  >
                    {route.edit ? (
                      <img alt="save icon" className="save-route-icon" src="/Assets/icons/saveicon.svg" />
                    ) : (
                      <div className="bi bi-pencil"></div>
                    )}
                  </button>

                  <button
                    title="delete route"
                    type="button"
                    className="delete-route-button"
                    onClick={(e) => (!activeDeleteDialog ? setActiveDeleteDialog(`delete_${route.id}`) : null)}
                  >
                    <div className="bi bi-trash3"></div>
                  </button>
                  {route.edit ? (
                    <button
                      title="close edit mode"
                      type="button"
                      className={`close-edit-route-button`}
                      onClick={() => closeEditMode(route)}
                    >
                      <div className="bi bi-x"></div>
                    </button>
                  ) : null}
                  <LinerServiceDeleteDialog
                    id={`delete_${route.id}` || ''}
                    visible={activeDeleteDialog === `delete_${route.id}`}
                    onConfirmClick={() => deleteRoute(route)}
                    onDenyClick={() => setActiveDeleteDialog(null)}
                  />
                </div>
              </div>
            ))
          ) : (
            <span className="no-routes-text">Inga tillagda rutter</span>
          )}
        </div>
      </div>

      <div className="liner-service-admin-routes-add">
        <div className="new-route-value-form">
          <label htmlFor={`agent_${newRoute.id}`}>Agent:</label>
            {agents.length > 0 &&
              <TypeaheadSearch
              id={`agent_${newRoute.id}`}
              resultList={agents.map((agent) => {
              return { id: agent.id, value: agent.name, label: agent.name, link: agent.link };
              })}
              defaultValue={{
                id: agents[0].id,
                value: agents[0].name,
                label: agents[0].name,
                link: agents[0].link,
              }}
              valueSelected={(item: TypeaheadSearchResultItem) => setItemValue(`agent_${newRoute.id}`, item)}
              placeholder="Sök här"
              />
            }
        </div>
        <div className="new-route-value-form">
            <label htmlFor={`shippingCompany_${newRoute.id}`}>Rederi:</label>
              {shippingCompanies.length > 0 &&
                <TypeaheadSearch
                id={`shippingCompany_${newRoute.id}`}
                resultList={shippingCompanies.map((shippingCompany) => {
                return {
                  id: shippingCompany.id,
                  value: shippingCompany.name,
                  label: shippingCompany.name,
                  link: shippingCompany.link,
                };
                })}
                defaultValue={{
                  id: shippingCompanies[0].id,
                  value: shippingCompanies[0].name,
                  label: shippingCompanies[0].name,
                  link: shippingCompanies[0].link,
                }}
                valueSelected={(item: TypeaheadSearchResultItem) => setItemValue(`shippingCompany_${newRoute.id}`, item)}
                placeholder="Sök här"
                />
             }
        </div>
        <div className="new-route-value-form">
          <label htmlFor={`routeFrequency_${newRoute.id}`}>Frekvens:</label>
          <LinerServiceFrequencySelector
            id={`routeFrequency_${newRoute.id}`}
            valueSelected={(item: TypeaheadSearchResultItem) => setItemValue(`routeFrequency_${newRoute.id}`, item)}
            defaultValue={{ id: generate(), label: '1/dag', value: '1/dag' }}
          />
        </div>
        <div className="new-route-value-form">
          <label htmlFor={`terminal_${newRoute.id}`}>Terminal:</label>
          {terminals.length > 0 &&     
            <TypeaheadSearch
            id={`terminal_${newRoute.id}`}
            resultList={terminals.map((terminal) => {
              return {
                id: terminal.id,
                value: terminal.name,
                label: terminal.name,
                link: terminal.link,
              };
            })}
            defaultValue={{
              id: terminals[0].id,
              value: terminals[0].name,
              label: terminals[0].name,
              link: terminals[0].link,
            }}
            valueSelected={(item: TypeaheadSearchResultItem) => setItemValue(`terminal_${newRoute.id}`, item)}
            placeholder="Sök här"
            />
         }
        </div>
        <div className="new-route-value-form">
          <label>Last:</label>
          <div className="cargo-group">
            <div>
              <label htmlFor={`cargo_container_${newRoute.id}`}>Container:</label>
              <input
                type="checkbox"
                id={`cargo_container_${newRoute.id}`}
                name="Last"
                defaultChecked={newRoute.container}
                onChange={(e) => setCheckboxValue(`container_${newRoute.id}`, e.currentTarget.checked)}
              />
            </div>
            <div>
              <label htmlFor={`cargo_roro_${newRoute.id}`}>Roro/Fordon:</label>
              <input
                type="checkbox"
                id={`cargo_roro_${newRoute.id}`}
                name="Roro/Fordon"
                onChange={(e) => setCheckboxValue(`roro_${newRoute.id}`, e.currentTarget.checked)}
              />
            </div>
            <div>
              <label htmlFor={`cargo_bulk_${newRoute.id}`}>Bulk:</label>
              <input
                type="checkbox"
                id={`cargo_bulk_${newRoute.id}`}
                name="Bulk"
                defaultChecked={newRoute.bulk}
                onChange={(e) => setCheckboxValue(`bulk_${newRoute.id}`, e.currentTarget.checked)}
              />
            </div>
          </div>
        </div>

        <button onClick={() => addRoute()} type="button" className="add-route-button">
          Lägg till rutt
        </button>
      </div>
    </div>
  );
};

export default LinerServiceRouteList;
